import React, { memo, useMemo } from 'react';
import {
    BookOpen16Regular,
    CalendarLtr16Regular,
    ChartMultiple16Regular,
    CheckmarkCircle16Regular,
    DocumentBulletList16Regular,
    Lightbulb16Regular,
    ShieldTask16Regular,
    Sparkle16Filled,
    Table16Regular,
    TargetArrow16Regular,
} from '@fluentui/react-icons';
import { RichMarkdown } from '../markdown';
import ResponseActionsFooter from '../ResponseActionsFooter';
import TimelineBlock from '../structured/TimelineBlock';
import ChecklistBlock from '../structured/ChecklistBlock';
import ComparisonTableBlock from '../structured/ComparisonTableBlock';
import { UnsupportedBlock } from '../structured/UnsupportedBlock';
import ReportSection from './ReportSection';
import ExecutiveSummaryCard from './ExecutiveSummaryCard';
import MetricsGrid from './MetricsGrid';
import FindingsList from './FindingsList';
import RecommendationCards from './RecommendationCards';
import RiskPanel from './RiskPanel';
import AppendixSection from './AppendixSection';
import {
    REPORT_SECTION_KEYS,
    hasConsultantReportContent,
    partitionBlocksForReport,
} from './reportLayoutUtils';
import styles from './consultantReport.module.css';

const SECTION_META = {
    [REPORT_SECTION_KEYS.EXECUTIVE_SUMMARY]: { title: 'Executive Summary', icon: Sparkle16Filled },
    [REPORT_SECTION_KEYS.KEY_METRICS]: { title: 'Key Metrics', icon: ChartMultiple16Regular },
    [REPORT_SECTION_KEYS.KEY_FINDINGS]: { title: 'Key Findings', icon: Lightbulb16Regular },
    [REPORT_SECTION_KEYS.SUPPORTING_EVIDENCE]: { title: 'Supporting Evidence', icon: DocumentBulletList16Regular },
    [REPORT_SECTION_KEYS.RISKS_GAPS]: { title: 'Risks & Gaps', icon: ShieldTask16Regular },
    [REPORT_SECTION_KEYS.RECOMMENDATIONS]: { title: 'Recommendations', icon: TargetArrow16Regular },
    [REPORT_SECTION_KEYS.NEXT_ACTIONS]: { title: 'Next Actions', icon: CheckmarkCircle16Regular },
    [REPORT_SECTION_KEYS.TIMELINE]: { title: 'Timeline', icon: CalendarLtr16Regular },
    [REPORT_SECTION_KEYS.COMPARISON_TABLES]: { title: 'Comparison Tables', icon: Table16Regular },
    [REPORT_SECTION_KEYS.CHECKLISTS]: { title: 'Checklists', icon: CheckmarkCircle16Regular },
    [REPORT_SECTION_KEYS.APPENDIX]: { title: 'Appendix', icon: BookOpen16Regular },
    [REPORT_SECTION_KEYS.UNKNOWN]: { title: 'Additional Analysis', icon: DocumentBulletList16Regular },
};

function ConsultantReport({
    blocks,
    fallbackContent,
    renderContext,
    reportActions = null,
    className = '',
}) {
    const sections = useMemo(() => partitionBlocksForReport(blocks), [blocks]);
    const showReport = hasConsultantReportContent(sections, reportActions);

    if (!showReport) {
        return (
            <RichMarkdown
                content={fallbackContent}
                citations={renderContext.citations}
                chunks={renderContext.chunks}
                citationScope={renderContext.citationScope}
                onCitationClick={renderContext.onCitationClick}
                className={className}
            />
        );
    }

    return (
        <article
            className={`${styles.consultantReport} ${className}`.trim()}
            role="document"
            aria-label="Consultant report">
            {sections[REPORT_SECTION_KEYS.EXECUTIVE_SUMMARY].length > 0 ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.EXECUTIVE_SUMMARY]}>
                    {sections[REPORT_SECTION_KEYS.EXECUTIVE_SUMMARY].map(block => (
                        <ExecutiveSummaryCard key={block.id} block={block} renderContext={renderContext} />
                    ))}
                </ReportSection>
            ) : null}

            {sections[REPORT_SECTION_KEYS.KEY_METRICS].length > 0 ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.KEY_METRICS]}>
                    <MetricsGrid blocks={sections[REPORT_SECTION_KEYS.KEY_METRICS]} renderContext={renderContext} />
                </ReportSection>
            ) : null}

            {sections[REPORT_SECTION_KEYS.KEY_FINDINGS].length > 0 ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.KEY_FINDINGS]}>
                    <FindingsList blocks={sections[REPORT_SECTION_KEYS.KEY_FINDINGS]} renderContext={renderContext} />
                </ReportSection>
            ) : null}

            {sections[REPORT_SECTION_KEYS.SUPPORTING_EVIDENCE].length > 0 ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.SUPPORTING_EVIDENCE]}>
                    {sections[REPORT_SECTION_KEYS.SUPPORTING_EVIDENCE].map(block => (
                        <UnsupportedBlock key={block.id} block={block} renderContext={renderContext} />
                    ))}
                </ReportSection>
            ) : null}

            {sections[REPORT_SECTION_KEYS.RISKS_GAPS].length > 0 ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.RISKS_GAPS]}>
                    <RiskPanel blocks={sections[REPORT_SECTION_KEYS.RISKS_GAPS]} renderContext={renderContext} />
                </ReportSection>
            ) : null}

            {sections[REPORT_SECTION_KEYS.RECOMMENDATIONS].length > 0 ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.RECOMMENDATIONS]}>
                    <RecommendationCards
                        blocks={sections[REPORT_SECTION_KEYS.RECOMMENDATIONS]}
                        renderContext={renderContext}
                    />
                </ReportSection>
            ) : null}

            {reportActions ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.NEXT_ACTIONS]}>
                    <div className={`${styles.nextActionsWrap} noPrint`}>
                        <ResponseActionsFooter {...reportActions} />
                    </div>
                </ReportSection>
            ) : null}

            {sections[REPORT_SECTION_KEYS.TIMELINE].length > 0 ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.TIMELINE]}>
                    {sections[REPORT_SECTION_KEYS.TIMELINE].map(block => (
                        <TimelineBlock key={block.id} block={block} renderContext={renderContext} />
                    ))}
                </ReportSection>
            ) : null}

            {sections[REPORT_SECTION_KEYS.COMPARISON_TABLES].length > 0 ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.COMPARISON_TABLES]}>
                    <div className={styles.consultantTableWrap}>
                        {sections[REPORT_SECTION_KEYS.COMPARISON_TABLES].map(block => (
                            <ComparisonTableBlock key={block.id} block={block} renderContext={renderContext} />
                        ))}
                    </div>
                </ReportSection>
            ) : null}

            {sections[REPORT_SECTION_KEYS.CHECKLISTS].length > 0 ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.CHECKLISTS]}>
                    {sections[REPORT_SECTION_KEYS.CHECKLISTS].map(block => (
                        <ChecklistBlock key={block.id} block={block} renderContext={renderContext} />
                    ))}
                </ReportSection>
            ) : null}

            {sections[REPORT_SECTION_KEYS.APPENDIX].length > 0 ? (
                <AppendixSection blocks={sections[REPORT_SECTION_KEYS.APPENDIX]} renderContext={renderContext} />
            ) : null}

            {sections[REPORT_SECTION_KEYS.UNKNOWN].length > 0 ? (
                <ReportSection {...SECTION_META[REPORT_SECTION_KEYS.UNKNOWN]}>
                    {sections[REPORT_SECTION_KEYS.UNKNOWN].map(block => (
                        <UnsupportedBlock key={block.id} block={block} renderContext={renderContext} />
                    ))}
                </ReportSection>
            ) : null}
        </article>
    );
}

export default memo(ConsultantReport);
